<template>
  <div>
    <!-- Search -->
    <div class="filter-bar">
      <div class="mb-3 flex flex-wrap items-center gap-4">
        <select
          v-model="category"
          class="tb-select w-auto flex-none"
          data-entity="producer-shoppable-categoryFilter"
        >
          <option
            v-for="(v, k) in categories"
            :key="k"
            :value="v"
          >
            {{ k }}
          </option>
        </select>

        <form
          class="tb-form-group flex min-w-0 flex-1 items-center gap-4"
          @submit.prevent="keywordSearch()"
        >
          <div class="shoppable-search min-w-0 flex-1">
            <i class="fas fa-search"></i>
            <input
              v-model="keyword"
              type="text"
              class="tb-input"
              placeholder="Search"
              :disabled="loading"
              ref="searchInput"
              data-entity="producer-shoppable-searchInput"
            >
            <button
              v-if="keyword"
              class="shoppable-search__clear"
              type="button"
              data-entity="producer-shoppable-clearSearch"
              @click="clearSearch"
            >
              <span class="fa fa-close"></span>
            </button>
          </div>
  
          <button
            type="submit"
            :disabled="loading || !keyword"
            class="tb-btn tb-btn-contained medium-em flex-none"
            data-entity="producer-shoppable-searchButton"
          >
            Search
          </button>
        </form>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap items-center gap-4">
        <div class="shoppable-filters flex-none">
          <div class="toggle-switch mb-0">
            <label class="toggle-label" for="primeFilter">
              <img :src="primeLogoPath" alt="Amazon Prime Logo" class="prime-logo">
              <span class="toggle-slider-wrapper">
                <input
                  type="checkbox"
                  id="primeFilter"
                  v-model="primeFilter"
                  true-value="amazon:prime-eligible"
                  data-entity="producer-shoppable-primeFilter"
                  class="tgb-sr-only"
                />
                <span class="toggle-slider"></span>
              </span>
            </label>
          </div>
        </div>

        <div class="shoppable-filters flex-none">
          <select
            v-model="genderFilter"
            class="tb-select w-auto"
            data-entity="producer-shoppable-genderFilter"
          >
            <option value="">All Genders</option>
            <option v-for="g in genders" :key="g" :value="g">{{ g }}</option>
          </select>
        </div>
        <div class="shoppable-filters flex-none">
          <select
            v-model="reviewFilter"
            class="tb-select w-auto"
            data-entity="producer-shoppable-genderFilter"
          >
            <option :value="null">All Reviews</option>
            <option v-for="(v,k) in reviews" :key="k" :value="v">{{ k }}</option>
          </select>
        </div>

        <div class="shoppable-filters amazon-price-filter-wrap flex min-w-0 flex-1 items-center max-w-[30%]">
          <p class="shoppable-filters__title flex-none">Price</p>

          <span class="amazon-price-filter-wrap__value">${{ priceLow }}</span>
          <div class="tb-range-slider tb-range-slider--round amazon-price-filter-wrap__slider flex-1">
            <input
              class="tb-range-slider__input"
              type="range"
              :min="0"
              :step="25"
              :max="maxPrice"
              v-model.number="priceLow"
              data-entity="producer-shoppable-lowPriceFilter"
            />

            <input
              class="tb-range-slider__input"
              type="range"
              :min="0"
              :step="25"
              :max="maxPrice"
              v-model.number="priceHigh"
              data-entity="producer-shoppable-highPriceFilter"
            />
          </div>
          <span class="amazon-price-filter-wrap__value">${{ priceHigh }}{{ priceHigh === maxPrice ? '+' : '' }}</span>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div
      class="shoppable-results mt-8 max-h-[50vh] overflow-y-auto"
      :class="{ 'min-h-[20rem]': searched || loading }"
    >
      <div
        v-if="loading"
        class="flex min-h-[20rem] items-center justify-center text-center"
      >
        <span class="fas fa-spinner fa-spin"></span>
      </div>

      <template v-if="results.length">
        <p class="text-os-low text-[13px] text-right">{{ this.total }} results found</p>
        <div
          v-for="r in results"
          :key="r.sourceId"
          class="shoppable-result"
          :class="{
            'shoppable-result': true,
          }"
        >
          <img
            :src="r.imageUrl"
            class="shoppable-result__image"
            alt=""
          >
          <div class="shoppable-result__info">
            <p class="shoppable-result__details">
              {{ r.price }} {{ r.currency }} 
              <span>(ID: {{ r.sourceId }})</span>
              <img
                v-if="r.sourceItem.Offers?.Listings[0]?.DeliveryInfo?.IsPrimeEligible || primeFilter"
                :src="primeLogoPath"
                alt="Amazon Prime Logo"
              >
            </p>
            <p
              class="shoppable-result__name ellipsis"
            >
              <a
                :href="r.sourceUrl"
                target="_blank"
                class="shoppable-result__link text-os-medium"
              >
                <span class="tgb-sr-only">Link to product page</span>
                <span class="fa fa-external-link"></span>
                <span>{{ r.name }}</span>
              </a>
            </p>
          </div>
          <button
            class="tb-btn tb-btn-contained medium-em"
            :class="{
              'high-em tb-btn-contained--override-disabled': savedItemLookup[r.sourceId],
            }"
            :disabled="savedItemLookup[r.sourceId]"
            :data-test="`producer-shoppable-selectProduct-${r.sourceId}`"
            @click="chooseItem(r)"
          >
            {{ savedItemLookup[r.sourceId] ? 'Saved' : 'Select' }}
          </button>
        </div>
        <div
          v-if="this.total > 10 && this.page < this.pages"
          class="shoppable-load-more"
        >
          <button
            type="submit"
            :disabled="loading"
            class="tb-btn tb-btn-contained medium-em shoppable-load-more__button"
            data-entity="producer-shoppable-loadMore"
            @click="loadMore"
          >
            Load More
          </button>
        </div>
      </template>

      <div
        v-else-if="noResults"
        class="text-os-low text-[13px] text-center"
      >
        <p class="text-bold">
          No products found.
        </p>
        <p>
          Check your spelling or try filtering for better results.
        </p>
      </div>
      <p
        v-else
        class="text-os-low text-[13px] text-center"
      >Search a product to get started</p>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';

import CatalogMixin from '@/js/mixins/shoppable-catalog';

export default {
  name: 'AmazonCatalog',
  mixins: [CatalogMixin],
  data() {
    return {
      limit: 10, // override limit from mixin
      category: 'All',
      categories: {
        'All Departments': 'All',
        'Baby': 'Baby',
        'Beauty & Personal Care': 'Beauty',
        'Clothing, Shoes & Jewelry': 'Fashion',
        'Electronics': 'Electronics',
        'Health, Household & Baby Care': 'HealthPersonalCare',
        'Home & Kitchen': 'HomeAndKitchen',
        'Office Products': 'OfficeProducts',
        'Pet Supplies': 'PetSupplies',
        'Sports & outdoors': 'SportsAndOutdoors',
        'Toys & games': 'ToysAndGames',
      },
      primeFilter: false,
      genderFilter: '',
      genders: [
        "Men's", "Women's", "Kid's", 'Baby',
      ],
      reviewFilter: null,
      reviews: {
        '4+ Stars': 'rating:4',
        '3+ Stars': 'rating:3',
        '2+ Stars': 'rating:2',
        '1+ Stars': 'rating:1',
      },
      priceLow: 0,
      priceHigh: 100,
      maxPrice: 100,
    };
  },

  computed: {
    priceRange() {
      if (this.priceHigh === this.maxPrice && this.priceLow === 0) {
        return '';
      } 
      
      if (this.priceHigh === this.maxPrice) {
        return `price:${this.priceLow}-`;
      }

      return `price:${this.priceLow}-${this.priceHigh}`;
    },
    allFilters() {
      return [
        this.reviewFilter,
        this.primeFilter,
        this.priceRange,
      ].filter(Boolean);
    },
    searchQuery() {
      return `${this.keyword} ${this.genderFilter}`.trim();
    },
    queryParams() {
      return {
        filters: this.allFilters.join(','),
        category: this.category,
      };
    },
    primeLogoPath() {
      return `${process.env.ASSET_CDN_BASE}/logos/amazon/prime-logo.svg`;
    },
  },

  created() {
    this.store = 'amazon'; // set data for mixin
  },
  mounted() {
    this.$refs.searchInput.focus();
  },

  watch: {
    allFilters: debounce(function() {
      this.keywordSearch();
    }, 500),
    priceLow(v) {
      if (v > this.priceHigh) {
        this.priceHigh = v;
      }
    },
    priceHigh(v) {
      if (v < this.priceLow) {
        this.priceLow = v;
      }
    },
  },
};
</script>

<style lang="scss">
.amazon-price-filter-wrap {
  gap: 0.5rem;

  &__title {
    white-space: nowrap;
  }

  &__value {
    white-space: nowrap;
    font-size: 0.6875rem;
    color: #9fb0b4;
  }

  &__slider {
    min-width: 0;
    width: 100%;
  }
}

.toggle-switch {
  display: flex;
  align-items: center;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 0;
}

.prime-logo {
  margin-right: 0.5rem;
  height: 0.9375rem;
}

.toggle-slider-wrapper {
  position: relative;
  width: 1.6875rem;
  height: 0.9375rem;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #969696;
  border-radius: 0.9375rem; 
  transition: 0.4s;
}

.toggle-slider:before {
  content: "";
  position: absolute;
  height: 0.6875rem;
  width: 0.6875rem;
  left: 2px; 
  bottom: 2px;
  background-color: white;
  border-radius: 50%;
  transition: 0.4s;
}

input:checked + .toggle-slider {
  background-color: #18C2ED; 
}

input:checked + .toggle-slider:before {
  transform: translateX(0.75rem); 
}
</style>
